import type { User } from '@workos-inc/authkit-js';
import { AuthKitProvider, useAuth as useWorkOSAuth } from '@workos-inc/authkit-react';
import type { ReactNode } from 'react';

/**
 * WorkOS AuthKit Context Provider
 *
 * Wraps the WorkOS AuthKitProvider with our app-specific configuration.
 * Provides authentication state and methods to child components.
 *
 * Usage in app entry point:
 * ```tsx
 * import { AuthProvider } from '@ophelos/ui/contexts/workos-auth-context';
 *
 * <AuthProvider clientId={env.VITE_WORKOS_CLIENT_ID}>
 *   <App />
 * </AuthProvider>
 * ```
 *
 * Usage in components:
 * ```tsx
 * import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
 *
 * function MyComponent() {
 *   const { user, isLoading, signIn, signOut } = useAuth();
 *   // ...
 * }
 * ```
 */

interface AuthProviderProps {
  children: ReactNode;
  clientId: string;
  redirectUri?: string;
  devMode?: boolean;
  /** Custom domain for WorkOS AuthKit API (production only, e.g., "auth.next.intrum.com") */
  apiHostname?: string;
  onRedirectCallback?: () => void;
}

export function AuthProvider({ children, clientId, redirectUri, devMode = false, apiHostname, onRedirectCallback }: AuthProviderProps) {
  return (
    <AuthKitProvider
      clientId={clientId}
      redirectUri={redirectUri}
      devMode={devMode}
      apiHostname={apiHostname}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </AuthKitProvider>
  );
}

/**
 * Hook to access WorkOS authentication state
 *
 * Returns:
 * - user: Authenticated user object from WorkOS
 * - isLoading: Boolean indicating if auth state is loading
 * - isAuthenticated: Boolean indicating if user is authenticated
 * - organizationId: Current organization ID (WorkOS org ID)
 * - role: User's role from WorkOS (e.g., 'system-admin', 'operator-admin')
 * - permissions: Array of permission strings from WorkOS
 * - signIn: Function to initiate sign-in flow
 * - signOut: Function to sign out current user
 * - getAccessToken: Function to get current JWT access token
 * - impersonator: Impersonation info (if admin is impersonating another user)
 *
 * @example
 * ```tsx
 * const { isAuthenticated, user, isLoading } = useAuth();
 *
 * if (isLoading) return <Loading />;
 * if (!isAuthenticated) return <Navigate to="/login" />;
 *
 * return <div>Welcome {user.firstName}!</div>;
 * ```
 */
export interface UseAuthReturn {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  organizationId: string | null;
  role: string | null;
  permissions: string[];
  signIn: ReturnType<typeof useWorkOSAuth>['signIn'];
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  switchToOrganization: (options: { organizationId: string }) => Promise<void>;
  impersonator: ReturnType<typeof useWorkOSAuth>['impersonator'];
}

export function useAuth(): UseAuthReturn {
  const auth = useWorkOSAuth();

  return {
    user: auth.user,
    isLoading: auth.isLoading,
    isAuthenticated: !!auth.user,
    organizationId: auth.organizationId,
    role: auth.role,
    permissions: auth.permissions,
    signIn: auth.signIn,
    signOut: auth.signOut,
    getAccessToken: auth.getAccessToken,
    switchToOrganization: auth.switchToOrganization,
    impersonator: auth.impersonator,
  };
}

/**
 * Hook to get access token for API calls
 *
 * Returns a function that retrieves the current JWT access token.
 * This token should be sent in the Authorization header for authenticated API requests.
 *
 * @example
 * ```tsx
 * const { getAccessToken } = useAccessToken();
 *
 * async function fetchData() {
 *   const token = await getAccessToken();
 *   const response = await fetch('/api/data', {
 *     headers: {
 *       Authorization: `Bearer ${token}`,
 *     },
 *   });
 * }
 * ```
 */
export function useAccessToken() {
  const { getAccessToken } = useAuth();
  return { getAccessToken };
}
